import { toZonedTime } from 'date-fns-tz';
import {
  type DayOfWeekEnum,
  EventAgeGroupsEnum,
  type EventsBoolExp,
  type GeographyComparisonExp,
  type LessonsBoolExp,
  PlayerFinderTimeOfDayEnum,
  SkillLevelValuesEnum,
} from 'types/generated/client';
import {
  dayOfWeekFromDate,
  expandAvailabilityDays,
  timesForDay,
} from 'utils/shared/playerAvailability';
import { getTimezone } from 'utils/shared/time/getTimezone';
import {
  type FindFilters,
  PRICE_MAX,
  PRICE_MIN,
  matchesNeighborhoods,
  timeOfDayFromHour,
  withinPriceRange,
} from 'screens/Find/filters';
import {
  DEFAULT_SEARCH_CENTER,
  FIND_RADIUS_MILES,
  neighborhoodAreas,
  searchAreas,
  withinMiles,
} from 'screens/Find/geo';
import type { FindGame } from 'screens/Find/types';
import { HomeDateRangeEnum } from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import type { TopOpportunityEvent } from 'screens/PlayerHomeV3/components/TopOpportunitiesSection/types';

// Filters-sheet rules for the home feed — DECISIONS-HOME-FEED.md §4 / §4a.

/** Radius ignored while a venue whitelist is active (fetch wide, filter by venue). */
const WHITELIST_FETCH_RADIUS_MILES = 100;

/** The radius the user actually chose (base + bonus), regardless of whitelist. */
export const chosenRadiusMiles = (sheet: FindFilters): number =>
  FIND_RADIUS_MILES + sheet.distanceBonusMiles;

/** Enough to catch a venue's own point; the venue-id whitelist does the exact match. */
const VENUE_AREA_RADIUS_MILES = 0.25;

/**
 * Where the feed queries look. Picked venues and neighborhoods together (either one
 * matches) replace the default market wherever they are; with neither, that market.
 */
export const feedAreas = (sheet: FindFilters): GeographyComparisonExp[] => {
  if (sheet.venues.length === 0) {
    return searchAreas(chosenRadiusMiles(sheet), sheet.neighborhoods);
  }
  const venueAreas = sheet.venues.flatMap((venue) =>
    venue.latitude != null && venue.longitude != null
      ? [withinMiles({ lat: venue.latitude, lng: venue.longitude }, VENUE_AREA_RADIUS_MILES)]
      : [],
  );
  return [
    // A venue without coordinates can't be located, so fall back to fetching wide.
    ...(venueAreas.length === sheet.venues.length
      ? venueAreas
      : [withinMiles(DEFAULT_SEARCH_CENTER, WHITELIST_FETCH_RADIUS_MILES)]),
    ...neighborhoodAreas(sheet.neighborhoods),
  ];
};

/** Client-side radius check for pre-fetched lists, measured from the search centre. */
export const withinChosenRadius = (
  searchDistanceMiles: number | null | undefined,
  sheet: FindFilters,
) =>
  sheet.venues.length > 0 ||
  sheet.neighborhoods.length > 0 ||
  searchDistanceMiles == null ||
  searchDistanceMiles <= chosenRadiusMiles(sheet);

const withinPrice = (priceMajorUnits: number, sheet: FindFilters) =>
  withinPriceRange(priceMajorUnits, sheet);

interface StartInfo {
  startDateTime: string;
  timezone?: string;
}

/** A Date whose local fields read in the venue's zone (browser zone when unknown). */
const inVenueZone = (date: Date, timezone?: string) => toZonedTime(date, getTimezone(timezone));

/** Weekday + hour of the start, in the venue's zone. */
const localStart = ({ startDateTime, timezone }: StartInfo) => {
  const zoned = inVenueZone(new Date(startDateTime), timezone);
  return { day: dayOfWeekFromDate(zoned), hour: zoned.getHours() };
};

const matchesTimes = (times: PlayerFinderTimeOfDayEnum[], hour: number) =>
  times.length === 0 ||
  times.includes(PlayerFinderTimeOfDayEnum.Flexible) ||
  times.includes(timeOfDayFromHour(hour));

/**
 * The availability pattern × date-pill rule (DECISIONS-HOME-FEED.md §4a).
 * ThisWeek/ThisMonth: strict intersection. Today: the pill wins — weekday
 * constraint dropped; today's time-of-day row applies (whether today is a chosen
 * day or picked directly in Today mode). All weekdays are read in the venue's zone.
 */
export const matchesAvailability = (
  item: StartInfo,
  sheet: FindFilters,
  dateRange: HomeDateRangeEnum,
  now = new Date(),
): boolean => {
  const { day, hour } = localStart(item);

  if (dateRange === HomeDateRangeEnum.Today) {
    const todayDay = dayOfWeekFromDate(inVenueZone(now, item.timezone));
    const times = todayTimes(todayDay, sheet);
    return matchesTimes(times, hour);
  }

  if (sheet.availabilityDays.length === 0) return true;
  if (!expandAvailabilityDays(sheet.availabilityDays).includes(day)) return false;
  return matchesTimes(timesForDay(day, sheet.availabilityDays, sheet.timesByDay), hour);
};

/** Today mode reads today's own row plus any shorthand covering it. */
export const todayTimes = (todayDay: DayOfWeekEnum, sheet: FindFilters) => {
  const direct = sheet.timesByDay[todayDay] ?? [];
  const viaPattern = expandAvailabilityDays(sheet.availabilityDays).includes(todayDay)
    ? timesForDay(todayDay, sheet.availabilityDays, sheet.timesByDay)
    : [];
  return Array.from(new Set([...direct, ...viaPattern]));
};

/** No skill list on the item = open to all levels. */
const matchesSkill = (levels: SkillLevelValuesEnum[] | undefined, sheet: FindFilters) =>
  sheet.skillLevels.length === 0 ||
  !levels?.length ||
  levels.some((level) => sheet.skillLevels.includes(level));

/**
 * Venues and neighborhoods are one "Location" facet: with both set, an item at a
 * chosen venue OR inside a chosen neighborhood passes. Neither set = no constraint.
 */
export const matchesLocation = (
  item: { latitude?: number; longitude?: number; neighborhoods?: { id: string }[] },
  venueIds: string[],
  sheet: FindFilters,
): boolean => {
  const hasVenues = sheet.venues.length > 0;
  const hasNeighborhoods = sheet.neighborhoods.length > 0;
  if (!hasVenues && !hasNeighborhoods) return true;
  const atVenue = hasVenues && sheet.venues.some((venue) => venueIds.includes(venue.id));
  const inNeighborhood = hasNeighborhoods && matchesNeighborhoods(item, sheet.neighborhoods);
  return atVenue || inNeighborhood;
};

export const matchesActivitySheet = (
  activity: TopOpportunityEvent,
  sheet: FindFilters,
  dateRange: HomeDateRangeEnum,
): boolean => {
  if (
    sheet.youthOnly &&
    activity.ageGroup !== EventAgeGroupsEnum.Youth &&
    activity.ageGroup !== EventAgeGroupsEnum.AllAges
  ) {
    return false;
  }
  if (!matchesLocation(activity, activity.venueId ? [activity.venueId] : [], sheet)) return false;
  // Events don't carry a skill level in the feed query; only lessons are narrowed.
  if (!matchesSkill(activity.skillLevels, sheet)) return false;
  if (!withinPrice(activity.priceMajorUnits ?? 0, sheet)) return false;
  return matchesAvailability(activity, sheet, dateRange);
};

export const matchesGameSheet = (
  game: FindGame,
  sheet: FindFilters,
  dateRange: HomeDateRangeEnum,
): boolean => {
  // Pickup games carry no age group, so the youth toggle hides them.
  if (sheet.youthOnly) return false;
  if (!matchesLocation(game, game.venueId ? [game.venueId] : [], sheet)) return false;
  if (!withinChosenRadius(game.searchDistanceMiles, sheet)) return false;
  if (!matchesSkill(game.skillLevel ? [game.skillLevel] : undefined, sheet)) return false;
  if (!withinPrice(game.priceFrom ?? 0, sheet)) return false;
  return matchesAvailability(game, sheet, dateRange);
};

/**
 * Server-side Location facet for the feed queries (empty object = no constraint).
 * A venue whitelist narrows the fetch to those venues, so chosen neighborhoods
 * are OR-ed in here too; otherwise rows inside them would never arrive for
 * `matchesLocation` to keep.
 */
export const venueWhitelistFilters = (sheet: FindFilters) => {
  const ids = sheet.venues.map((venue) => venue.id);
  if (ids.length === 0) return { events: {}, lessons: {} };
  const nearNeighborhood = neighborhoodAreas(sheet.neighborhoods);
  return {
    events: {
      _or: [{ venue: { id: { _in: ids } } }, ...nearNeighborhood.map((geometry) => ({ geometry }))],
    } as EventsBoolExp,
    lessons: {
      _or: [
        { venue: { id: { _in: ids } } },
        ...nearNeighborhood.flatMap((geometry) => [
          { venue: { geometry } },
          { userCustomCourt: { geometry } },
        ]),
      ],
    } as LessonsBoolExp,
  };
};

/** True when the sheet's venue whitelist replaces the location pill. */
export const isVenueWhitelistActive = (sheet: FindFilters) => sheet.venues.length > 0;
